import React, { useState } from "react";
import "./StudentLearningPlanSection.css";

const TUITION_PER_YEAR = 1129800; // 1,129,800円/年

// 実運用に合わせた詳細
const STEP_DETAILS = {
  basic: [
    "動画学習：毎日 約3時間（時間帯は自由・オンデマンド視聴）",
    "月・水・金 18:00–18:30：進捗チェック／質問対応／ミニ議論・グループワーク（メタバース）",
    "内容：動画の内容についての理解確認、補講、個別質問対応等",
  ],
  practice: [
    "動画課題＋実装課題：Web/アプリ、AI、データサイエンス各分野の小～中規模課題に取り組む",
    "“コードを書く”を徹底：API連携、DB、前処理・可視化、LLM/MLモデル利用、ミニサービスの試作まで",
    "個人 → ペア → 小チームで段階的にスケール。実力に合わせたサポート",
    "週次：スプリントレビュー（成果共有・KPT・翌週計画）",
  ],
  internship: [
    "企業/自治体の実課題に取り組む実務実習（PoC/実証実験）",
    "納期・完成度は“実験水準”でもOK、ただし、就職・フリーランス実績のための品質を目指す",
    "講師がPMとして伴走：要件整理、スケジュール、品質・リスク管理、対外折衝をサポート、クオリティ管理",
    "成果物：課題解決プロトタイプ、実装レポート、デモ、振り返り資料（ポートフォリオ化）",
  ],
};

const PLANS = {
  two: {
    key: "two",
    label: "2年制",
    years: 2,
    steps: [
      { no: 1, months: 3,  title: "基礎習得", desc: "基礎知識の習得と個別サポート", detail: STEP_DETAILS.basic },
      { no: 2, months: 6,  title: "実践演習", desc: "専門技術の実践的な課題演習",     detail: STEP_DETAILS.practice },
      { no: 3, months: 12, title: "実務実習", desc: "企業・自治体との協力による実務実習", detail: STEP_DETAILS.internship },
    ],
  },
  one: {
    key: "one",
    label: "1年制",
    years: 1,
    steps: [
      { no: 1, months: 3, title: "基礎習得", desc: "基礎知識の習得と個別サポート", detail: STEP_DETAILS.basic },
      { no: 2, months: 3, title: "実践演習", desc: "専門技術の実践的な課題演習",     detail: STEP_DETAILS.practice },
      { no: 3, months: 6, title: "実務実習", desc: "企業・自治体との協力による実務実習", detail: STEP_DETAILS.internship },
    ],
  },
};

const INCLUDED1 = [
  "学習管理システム",
  "メタバース空間(教室・自習室)",
  "チャットツール Discord",
];
const INCLUDED2 = [
  "ChatGPT他AIツールの利用料",
];

function Yen(n) {
  return n.toLocaleString("ja-JP");
}

function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0-18 0Z"/>
      <path fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            d="M8 12l3 3l5-6"/>
    </svg>
  );
}

export default function StudentLearningPlanSection() {
  const [active, setActive] = useState(PLANS.two.key);
  const plan = active === "two" ? PLANS.two : PLANS.one;

  return (
	<div className="learningPlan__background">
		<div className="learningPlan" aria-labelledby="learningPlan-title">
		<h2 id="learningPlan-title" className="learningPlan__title">学習スケジュール</h2>

		{/* Segmented switch（既存のdivタブを活かす） */}
		<div className="learningPlan__switch" role="tablist" aria-label="学習プラン選択">
			{Object.values(PLANS).map(p => (
			<div
				key={p.key}
				role="tab"
				aria-selected={active === p.key}
				className={`learningPlan__switchBtn ${active === p.key ? "is-active" : ""}`}
				onClick={() => setActive(p.key)}
			>
				{p.label}
			</div>
			))}
		</div>

		<div className="learningPlan__grid">
			{/* LEFT: flow */}
			<div className="learningPlan__left">
			<h3 className="learningPlan__subTitle">学習の流れ</h3>

			<ol className="learningPlan__steps">
				{plan.steps.map((s) => (
				<li key={s.no} className="learningPlan__step">
					<div className="learningPlan__stepNo">{s.no}</div>
					<div className="learningPlan__stepBody">
					<div className="learningPlan__stepTop">
						<span className="learningPlan__chip">{s.months}ヶ月</span>
						<span className="learningPlan__stepTitle">{s.title}</span>
						<span className="learningPlan__stepDesc">{s.desc}</span>
					</div>


					{/* 折りたたみ 詳細 */}
					{s.detail && (
						<details className="learningPlan__details">
						<summary>詳細を見る</summary>
						<ul>
							{s.detail.map((d, i) => <li key={i}>{d}</li>)}
						</ul>
						</details>
					)}
					</div>
				</li>
				))}
			</ol>

			{/* 週間スケジュール（基礎習得の例） */}
			{/* <div className="learningPlan__weekly">
				<h4 className="learningPlan__weeklyTitle">週間スケジュール（基礎習得の例）</h4>
				<ul className="learningPlan__weeklyList">
				<li><span className="dow">毎日</span> <span className="dot"></span> 動画学習 3時間（時間帯は自由・オンデマンド）</li>
				<li><span className="dow">月・水・金</span> <span className="dot"></span> 18:00–18:30 進捗チェック／質問／グループワーク（メタバース）</li>
				<li><span className="dow">週末</span> <span className="dot"></span> ふりかえり・補講・自主制作（任意）</li>
				</ul>
			</div> */}
			</div>

			{/* RIGHT: cost panel */}
			<aside className="learningPlan__panel">
			<h3 className="learningPlan__panelTitle">費用</h3>
			<div className="learningPlan__price">
				{Yen(TUITION_PER_YEAR)}円 <span>× {plan.years}年</span>
			</div>

			<div className="learningPlan__includesTitle">こちらで準備するもの：</div>
			<ul className="learningPlan__includes">
				{INCLUDED1.map((t, i) => (
				<li key={i}>
					<CheckIcon />
					<span>{t}</span>
				</li>
				))}
			</ul>
			<div className="learningPlan__includesTitle">別途費用が必要なもの：</div>
			<ul className="learningPlan__includes">
				{INCLUDED2.map((t, i) => (
				<li key={i}>
					<CheckIcon />
					<span>{t}</span>
				</li>
				))}
			</ul>

			<a
				className="learningPlan__cta"
				href="https://forms.gle/Bt2494z8zmQUAp5v5"
				target="_blank"
				rel="noopener noreferrer"
			>
				入学を申し込む
			</a>
			</aside>
		</div>
		</div>
	</div>
  );
}
